import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import { FixBugDialog, FIX_BUG_WORKFLOW_INSTRUCTIONS } from '../components/FixBugDialog';
import type { AppItem, Conversation, LogEvent, ModelConfig } from '../types';
import { reportError } from '../utils/errors';
import LogViewerPageFeedback from './LogViewerPageFeedback';
import './LogViewerPage.css';

type AppsResponse = AppItem[] | { apps?: AppItem[] | null };

function normalizeAppsResponse(data: AppsResponse): AppItem[] {
  return Array.isArray(data) ? data : data.apps ?? [];
}

export default function LogViewerPage({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const location = useLocation();
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [apps, setApps] = useState<AppItem[]>([]);
  const [appId, setAppId] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [level, setLevel] = useState('');
  const [levelMin, setLevelMin] = useState('');
  const [kind, setKind] = useState('');
  const [traceId, setTraceId] = useState('');
  const [requestId, setRequestId] = useState('');
  const [spanId, setSpanId] = useState('');
  const [parentSpanId, setParentSpanId] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(false);
  const [pendingLoads, setPendingLoads] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [queryInitialized, setQueryInitialized] = useState(false);
  const navigate = useNavigate();
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [fixBugOpen, setFixBugOpen] = useState(false);

  useEffect(() => {
    apiClient
      .get<AppsResponse>('/api/apps')
      .then((data) => setApps(normalizeAppsResponse(data)))
      .catch((err) => reportError('Failed to load apps', err, setError));
    apiClient
      .get<ModelConfig[]>('/api/me/models')
      .then((data) => setModels(data))
      .catch((err) => console.error('Failed to load models', err));
  }, [apiClient, setError]);

  const formatTimestamp = (value: string) =>
    value ? new Date(value).toLocaleString() : '-';

  const toggleExpanded = (id: string) => {
    setExpanded((current) => ({ ...current, [id]: !current[id] }));
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      reportError('Failed to copy value', err, setError);
    }
  };

  const copyFilteredLogs = async () => {
    const params = new URLSearchParams();
    if (appId) {
      params.set('app_id', appId);
    }
    if (sourceFilter) {
      params.set('from_app', sourceFilter);
    }
    if (level) {
      params.set('level', level);
    } else if (levelMin) {
      params.set('level_min', levelMin);
    }
    if (kind) {
      params.set('kind', kind);
    }
    if (traceId.trim()) {
      params.set('trace_id', traceId.trim());
    }
    if (requestId.trim()) {
      params.set('request_id', requestId.trim());
    }
    if (spanId.trim()) {
      params.set('span_id', spanId.trim());
    }
    if (parentSpanId.trim()) {
      params.set('parent_span_id', parentSpanId.trim());
    }
    if (search.trim()) {
      params.set('q', search.trim());
    }
    if (from) {
      const iso = new Date(from).toISOString();
      params.set('from', iso);
    }
    if (to) {
      const iso = new Date(to).toISOString();
      params.set('to', iso);
    }
    params.set('limit', String(limit));

    const payload = {
      filters: {
        appId,
        sourceFilter,
        level,
        levelMin,
        kind,
        traceId,
        requestId,
        spanId,
        parentSpanId,
        search,
        from,
        to,
        limit
      },
      query: params.toString(),
      logs
    };
    await copyText(JSON.stringify(payload, null, 2));
  };

  const copyExpandedLogs = async () => {
    const expandedLogs = logs.filter((log) => expanded[log.id]);
    if (expandedLogs.length === 0) {
      setError('No logs are currently expanded. Click "Details" on a log entry to expand it first.');
      return;
    }
    const payload = {
      note: 'Only expanded logs are included',
      count: expandedLogs.length,
      total: logs.length,
      logs: expandedLogs
    };
    await copyText(JSON.stringify(payload, null, 2));
  };

  const formatCommand = (command: string) =>
    command
      .replace(/;\s+/g, ';\n')
      .replace(/\s&&\s/g, ' &&\n')
      .replace(/\s\|\|\s/g, ' ||\n');

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

  const extractDockerRun = (fields?: Record<string, unknown> | null) => {
    if (!fields || !isRecord(fields.docker_run)) {
      return null;
    }
    return fields.docker_run as Record<string, unknown>;
  };

  const formatInlineValue = (value: unknown) => {
    if (typeof value === 'string') {
      return value;
    }
    return JSON.stringify(value);
  };

  const renderCommandBlock = (label: string, value: string) => {
    const pretty = formatCommand(value);
    return (
      <div className="log-command">
        <div className="log-command-header">
          <strong>{label}</strong>
          <span className="hint">masked</span>
        </div>
        <div className="row">
          <button
            type="button"
            className="mini-button secondary"
            onClick={() => copyText(value)}
          >
            Copy raw
          </button>
          <button
            type="button"
            className="mini-button secondary"
            onClick={() => copyText(pretty)}
          >
            Copy formatted
          </button>
        </div>
        <pre className="log-command-pre">{pretty}</pre>
      </div>
    );
  };

  const renderDockerRunDetails = (fields?: Record<string, unknown> | null) => {
    const dockerRun = extractDockerRun(fields);
    if (!dockerRun) {
      return null;
    }
    const fieldCommand =
      fields && typeof fields.command === 'string' ? fields.command : '';
    const command =
      fieldCommand || (typeof dockerRun.command === 'string' ? dockerRun.command : '');
    const fieldInnerScript =
      fields && typeof fields.inner_script === 'string' ? fields.inner_script : '';
    const innerScript =
      fieldInnerScript ||
      (typeof dockerRun.stdin_script === 'string' ? dockerRun.stdin_script : '');
    const mounts = Array.isArray(dockerRun.mounts) ? dockerRun.mounts : [];
    const args = Array.isArray(dockerRun.args) ? dockerRun.args : [];
    const env = isRecord(dockerRun.env) ? dockerRun.env : null;
    return (
      <div className="log-command-card">
        <div className="log-command-meta">
          <strong>Repro command</strong>
          <span className="log-command-tag">masked</span>
          <span className="hint">
            Replace placeholders like __token__ or __api_key__ before reproducing.
          </span>
        </div>
        {command ? renderCommandBlock('Command', command) : null}
        <div className="log-command-grid">
          {typeof dockerRun.image === 'string' && (
            <div>
              <div className="log-command-label">Image</div>
              <div className="mono">{dockerRun.image}</div>
            </div>
          )}
          {typeof dockerRun.workdir === 'string' && (
            <div>
              <div className="log-command-label">Workdir</div>
              <div className="mono">{dockerRun.workdir}</div>
            </div>
          )}
          {typeof dockerRun.entrypoint === 'string' && (
            <div>
              <div className="log-command-label">Entrypoint</div>
              <div className="mono">{dockerRun.entrypoint}</div>
            </div>
          )}
          {typeof dockerRun.extra_args === 'string' && (
            <div>
              <div className="log-command-label">Extra args</div>
              <div className="mono">{dockerRun.extra_args}</div>
            </div>
          )}
        </div>
        {mounts.length > 0 && (
          <div>
            <div className="log-command-label">Mounts</div>
            <div className="log-command-list">
              {mounts.map((mount, index) => (
                <div key={`${mount}-${index}`} className="mono">
                  {formatInlineValue(mount)}
                </div>
              ))}
            </div>
          </div>
        )}
        {args.length > 0 && (
          <div>
            <div className="log-command-label">Args</div>
            <div className="log-command-list">
              {args.map((arg, index) => (
                <div key={`${arg}-${index}`} className="mono">
                  {formatInlineValue(arg)}
                </div>
              ))}
            </div>
          </div>
        )}
        {env && (
          <div>
            <div className="log-command-label">Env (masked)</div>
            <div className="log-command-list">
              {Object.entries(env).map(([key, value]) => (
                <div key={key} className="mono">
                  {key}={formatInlineValue(value)}
                </div>
              ))}
            </div>
          </div>
        )}
        {innerScript ? renderCommandBlock('Inner script', innerScript) : null}
      </div>
    );
  };

  const renderFieldValue = (key: string, value: unknown) => {
    if (value === null || value === undefined) {
      return <span className="hint">-</span>;
    }
    if (key === 'inner_script' && typeof value === 'string') {
      // The inner_script is already decoded and masked on the backend
      const pretty = formatCommand(value);
      return (
        <div className="log-command">
          <div className="log-base64-header">
            Inner script (decoded on backend, secrets masked)
          </div>
          <div className="row">
            <button
              type="button"
              className="mini-button secondary"
              onClick={() => copyText(value)}
            >
              Copy script
            </button>
            <button
              type="button"
              className="mini-button secondary"
              onClick={() => copyText(pretty)}
            >
              Copy formatted
            </button>
          </div>
          <pre className="log-command-pre">{pretty}</pre>
        </div>
      );
    }
    if (key === 'command' && typeof value === 'string') {
      const pretty = formatCommand(value);
      return (
        <div className="log-command">
          <div className="row">
            <button
              type="button"
              className="mini-button secondary"
              onClick={() => copyText(value)}
            >
              Copy raw
            </button>
            <button
              type="button"
              className="mini-button secondary"
              onClick={() => copyText(pretty)}
            >
              Copy formatted
            </button>
          </div>
          <pre className="log-command-pre">{pretty}</pre>
        </div>
      );
    }
    if (typeof value === 'string') {
      if (value.length > 140 || value.includes('\n')) {
        return <pre>{value}</pre>;
      }
      return <span className="mono">{value}</span>;
    }
    return <pre>{JSON.stringify(value, null, 2)}</pre>;
  };

  const renderFields = (
    fields?: Record<string, unknown> | null,
    excludedKeys: string[] = []
  ) => {
    const entries = fields
      ? Object.entries(fields).filter(([key]) => !excludedKeys.includes(key))
      : [];
    if (entries.length === 0) {
      return <div className="hint">No fields</div>;
    }
    return (
      <div className="log-field-grid">
        {entries.map(([key, value]) => (
          <React.Fragment key={key}>
            <div className="log-field-key mono">{key}</div>
            <div className="log-field-value">{renderFieldValue(key, value)}</div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  type LogFilters = {
    appId: string;
    sourceFilter: string;
    level: string;
    levelMin: string;
    kind: string;
    traceId: string;
    requestId: string;
    spanId: string;
    parentSpanId: string;
    search: string;
    from: string;
    to: string;
    limit: number;
  };

  const parseDateInput = (value: string | null) => {
    if (!value) {
      return '';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const pad = (part: number) => String(part).padStart(2, '0');
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(
      parsed.getHours()
    )}:${pad(parsed.getMinutes())}`;
  };

  const parseQueryFilters = useCallback((searchParams: string): Partial<LogFilters> => {
    const params = new URLSearchParams(searchParams);
    const parsedLimit = params.get('limit');
    const limitValue = parsedLimit ? Number(parsedLimit) : undefined;
    return {
      appId: params.get('app_id') ?? '',
      sourceFilter: params.get('from_app') ?? '',
      level: params.get('level') ?? '',
      levelMin: params.get('level_min') ?? '',
      kind: params.get('kind') ?? '',
      traceId: params.get('trace_id') ?? '',
      requestId: params.get('request_id') ?? '',
      spanId: params.get('span_id') ?? '',
      parentSpanId: params.get('parent_span_id') ?? '',
      search: params.get('q') ?? '',
      from: parseDateInput(params.get('from')),
      to: parseDateInput(params.get('to')),
      limit: limitValue && !Number.isNaN(limitValue) ? limitValue : undefined
    };
  }, []);

  // buildQuery needs to be stable for useCallback to work properly
  // We'll inline it into loadLogs instead
  const includeUnassignedForSelectedApp = useMemo(() => {
    if (!appId) {
      return false;
    }
    return apps.some((app) => app.id === appId && app.is_self);
  }, [appId, apps]);

  const loadLogs = useCallback(() => {
    setLoading(true);
    const resolved = {
      appId,
      sourceFilter,
      level,
      levelMin,
      kind,
      traceId,
      requestId,
      spanId,
      parentSpanId,
      search,
      from,
      to,
      limit
    };
    const params = new URLSearchParams();
    if (resolved.appId) {
      params.set('app_id', resolved.appId);
      if (includeUnassignedForSelectedApp) {
        params.set('include_unassigned', 'true');
      }
    }
    if (resolved.sourceFilter) {
      params.set('from_app', resolved.sourceFilter);
    }
    if (resolved.level) {
      params.set('level', resolved.level);
    } else if (resolved.levelMin) {
      params.set('level_min', resolved.levelMin);
    }
    if (resolved.kind) {
      params.set('kind', resolved.kind);
    }
    if (resolved.traceId.trim()) {
      params.set('trace_id', resolved.traceId.trim());
    }
    if (resolved.requestId.trim()) {
      params.set('request_id', resolved.requestId.trim());
    }
    if (resolved.spanId.trim()) {
      params.set('span_id', resolved.spanId.trim());
    }
    if (resolved.parentSpanId.trim()) {
      params.set('parent_span_id', resolved.parentSpanId.trim());
    }
    if (resolved.search.trim()) {
      params.set('q', resolved.search.trim());
    }
    if (resolved.from) {
      const iso = new Date(resolved.from).toISOString();
      params.set('from', iso);
    }
    if (resolved.to) {
      const iso = new Date(resolved.to).toISOString();
      params.set('to', iso);
    }
    params.set('limit', String(resolved.limit));
    const query = params.toString();

    setPendingLoads((current) => current + 1);
    apiClient
      .get<LogEvent[]>(`/api/logs${query ? `?${query}` : ''}`)
      .then((data) => {
        setLogs(data ?? []);
        setHasLoaded(true);
        setLoadFailed(false);
        setError(null);
      })
      .catch((err) => {
        setLoadFailed(true);
        reportError('Failed to load logs', err, setError);
      })
      .finally(() => {
        setPendingLoads((current) => current - 1);
        setLoading(false);
      });
  }, [apiClient, appId, sourceFilter, level, levelMin, kind, traceId, requestId, spanId, parentSpanId, search, from, to, limit, includeUnassignedForSelectedApp, setError]);

  const applyQuickFilter = (overrides: Partial<LogFilters>) => {
    if (overrides.appId !== undefined) {
      setAppId(overrides.appId);
    }
    if (overrides.sourceFilter !== undefined) {
      setSourceFilter(overrides.sourceFilter);
    }
    if (overrides.level !== undefined) {
      setLevel(overrides.level);
      if (overrides.level) {
        setLevelMin('');
      }
    }
    if (overrides.levelMin !== undefined) {
      setLevelMin(overrides.levelMin);
      if (overrides.levelMin) {
        setLevel('');
      }
    }
    if (overrides.kind !== undefined) {
      setKind(overrides.kind);
    }
    if (overrides.traceId !== undefined) {
      setTraceId(overrides.traceId);
    }
    if (overrides.requestId !== undefined) {
      setRequestId(overrides.requestId);
    }
    if (overrides.spanId !== undefined) {
      setSpanId(overrides.spanId);
      if (overrides.parentSpanId === undefined) {
        setParentSpanId('');
      }
    }
    if (overrides.parentSpanId !== undefined) {
      setParentSpanId(overrides.parentSpanId);
      if (overrides.spanId === undefined) {
        setSpanId('');
      }
    }
    if (overrides.search !== undefined) {
      setSearch(overrides.search);
    }
    if (overrides.from !== undefined) {
      setFrom(overrides.from);
    }
    if (overrides.to !== undefined) {
      setTo(overrides.to);
    }
    if (overrides.limit !== undefined) {
      setLimit(overrides.limit);
    }
  };

  const resetFilters = () => {
    setAppId('');
    setSourceFilter('');
    setLevel('');
    setLevelMin('');
    setKind('');
    setTraceId('');
    setRequestId('');
    setSpanId('');
    setParentSpanId('');
    setSearch('');
    setFrom('');
    setTo('');
    setLimit(200);
  };

  // Parse URL params and load logs on mount and when location.search changes
  useEffect(() => {
    const overrides = parseQueryFilters(location.search);
    if (overrides.appId !== undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAppId(overrides.appId);
    }
    if (overrides.sourceFilter !== undefined) {
      setSourceFilter(overrides.sourceFilter);
    }
    if (overrides.level !== undefined) {
      setLevel(overrides.level);
      if (overrides.level) {
        setLevelMin('');
      }
    }
    if (overrides.levelMin !== undefined) {
      setLevelMin(overrides.levelMin);
      if (overrides.levelMin) {
        setLevel('');
      }
    }
    if (overrides.kind !== undefined) {
      setKind(overrides.kind);
    }
    if (overrides.traceId !== undefined) {
      setTraceId(overrides.traceId);
    }
    if (overrides.requestId !== undefined) {
      setRequestId(overrides.requestId);
    }
    if (overrides.spanId !== undefined) {
      setSpanId(overrides.spanId);
    }
    if (overrides.parentSpanId !== undefined) {
      setParentSpanId(overrides.parentSpanId);
    }
    if (overrides.search !== undefined) {
      setSearch(overrides.search);
    }
    if (overrides.from !== undefined) {
      setFrom(overrides.from);
    }
    if (overrides.to !== undefined) {
      setTo(overrides.to);
    }
    if (overrides.limit !== undefined) {
      setLimit(overrides.limit);
    }
    setQueryInitialized(true);
  }, [location.search, parseQueryFilters]);

  // Load logs whenever filter state changes
  // This runs after the URL params are parsed and state is updated.
  useEffect(() => {
    if (!queryInitialized) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadLogs();
  }, [loadLogs, queryInitialized]);

  const hasTraceFilter = traceId.trim().length > 0;
  const selectedApp = useMemo(() => apps.find((app) => app.id === appId), [apps, appId]);
  const levelFilterValue = level ? `exact:${level}` : levelMin ? `min:${levelMin}` : '';
  const setLevelFilterValue = (value: string) => {
    if (!value) {
      setLevel('');
      setLevelMin('');
      return;
    }
    const [mode, selectedLevel] = value.split(':');
    if (mode === 'min') {
      setLevel('');
      setLevelMin(selectedLevel ?? '');
    } else {
      setLevel(selectedLevel ?? '');
      setLevelMin('');
    }
  };

  return (
    <section className="stack log-viewer">
      <div className="card full">
        <h2>Log Viewer</h2>
        <p>Search logs, metrics, and traces ingested via OTLP.</p>
        <div className="detail-grid">
          <label className="stack">
            App
            <select
              aria-label="App"
              value={appId}
              onChange={(event) => setAppId(event.target.value)}
            >
              <option value="">All apps</option>
              {apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </label>
          <label className="stack">
            Source
            <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
              <option value="">All sources</option>
              <option value="true">From app</option>
              <option value="false">Control plane / OTLP</option>
            </select>
          </label>
          <label className="stack">
            Level
            <select
              value={levelFilterValue}
              onChange={(event) => setLevelFilterValue(event.target.value)}
            >
              <option value="">Any</option>
              <option value="exact:error">Error only</option>
              <option value="exact:warn">Warn only</option>
              <option value="exact:info">Info only</option>
              <option value="exact:debug">Debug only</option>
              <option value="exact:trace">Trace only</option>
              <option value="min:error">Error and above</option>
              <option value="min:warn">Warn and above</option>
              <option value="min:info">Info and above</option>
              <option value="min:debug">Debug and above</option>
              <option value="min:trace">Trace and above</option>
            </select>
          </label>
          <label className="stack">
            Kind
            <select value={kind} onChange={(event) => setKind(event.target.value)}>
              <option value="">Any</option>
              <option value="log">Log</option>
              <option value="metric">Metric</option>
              <option value="trace">Trace</option>
              <option value="event">Event</option>
            </select>
          </label>
          <label className="stack">
            Search
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="message, metric, attribute..."
            />
          </label>
          <label className="stack">
            Trace ID
            <input
              value={traceId}
              onChange={(event) => setTraceId(event.target.value)}
              placeholder="trace_id"
            />
          </label>
          <label className="stack">
            Request ID
            <input
              value={requestId}
              onChange={(event) => setRequestId(event.target.value)}
              placeholder="request_id"
            />
          </label>
          <label className="stack">
            Span ID
            <input
              value={spanId}
              onChange={(event) => setSpanId(event.target.value)}
              placeholder="span_id"
            />
          </label>
          <label className="stack">
            Parent Span ID
            <input
              value={parentSpanId}
              onChange={(event) => setParentSpanId(event.target.value)}
              placeholder="parent_span_id"
            />
          </label>
          <label className="stack">
            From
            <input
              type="datetime-local"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </label>
          <label className="stack">
            To
            <input
              type="datetime-local"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </label>
          <label className="stack">
            Limit
            <input
              type="number"
              min={1}
              max={1000}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
            />
          </label>
        </div>
        <div className="row log-viewer-actions">
          <button onClick={() => loadLogs()} disabled={loading}>
            {loading ? 'Loading…' : 'Load logs'}
          </button>
          <button className="secondary" onClick={resetFilters} disabled={loading}>
            Reset
          </button>
          <button className="secondary" onClick={copyFilteredLogs} disabled={loading}>
            Copy filtered logs
          </button>
          <button className="secondary" onClick={copyExpandedLogs} disabled={loading}>
            Copy expanded ({Object.keys(expanded).filter((id) => expanded[id]).length})
          </button>
          {selectedApp && (
            <Link className="link-button" to={`/apps/${selectedApp.id}`}>
              Start conversation
            </Link>
          )}
          {(appId || sourceFilter || traceId || requestId || spanId || parentSpanId || search || kind || level || levelMin) && (
            <button
              className="link-button secondary"
              onClick={() => setFixBugOpen(true)}
              disabled={loading}
            >
              Start from current query
            </button>
          )}
        </div>
      </div>

      <div className="card full">
        <div className="card-header">
          <h3 id="log-viewer-events-heading">Recent events</h3>
          <span className="hint">{logs.length} entries</span>
        </div>
        <LogViewerPageFeedback
          pending={pendingLoads > 0 || (!hasLoaded && !loadFailed)}
          failed={loadFailed}
          count={logs.length}
        />
        <div
          className="table-scroll log-table-scroll"
          role="region"
          aria-labelledby="log-viewer-events-heading"
          tabIndex={0}
        >
          <table className="log-table" aria-labelledby="log-viewer-events-heading">
            <colgroup>
              <col className="log-table__col-time" />
              <col className="log-table__col-level" />
              <col className="log-table__col-kind" />
              <col className="log-table__col-entity" />
              <col className="log-table__col-stage" />
              <col className="log-table__col-message" />
              <col className="log-table__col-service" />
              <col className="log-table__col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Kind</th>
                <th>App</th>
                <th>Stage</th>
                <th>Message</th>
                <th>Service</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const levelClass = log.level ? `log-level log-level--${log.level}` : 'log-level';
                const appLabel = log.app_name || log.app_slug || log.app_id || '-';
                const stageLabel = log.stage_name || log.stage_id || '-';
                const dockerRunDetails = renderDockerRunDetails(log.fields);
                const excludeKeys = dockerRunDetails
                  ? ['command', 'inner_script', 'docker_run']
                  : [];
                const isFocusedSpan =
                  spanId.trim().length > 0 &&
                  log.span_id &&
                  spanId.trim() === log.span_id &&
                  (!hasTraceFilter || traceId.trim() === (log.trace_id ?? ''));
                return (
                  <React.Fragment key={log.id}>
                    <tr className="log-table__row">
                      <td className="log-table__time" data-label="Time">{formatTimestamp(log.ts)}</td>
                      <td className={levelClass} data-label="Level">{log.level ?? '-'}</td>
                      <td className="log-table__kind" data-label="Kind">{log.kind}</td>
                      <td className="mono log-table__entity" data-label="App">{appLabel}</td>
                      <td className="mono log-table__entity" data-label="Stage">{stageLabel}</td>
                      <td className="log-table__message" data-label="Message">{log.message ?? log.metric_name ?? log.event_name ?? '-'}</td>
                      <td className="log-table__service" data-label="Service">{log.service ?? '-'}</td>
                      <td className="log-table__actions" data-label="Actions">
                        <div className="row log-row-actions">
                          {log.span_id && !isFocusedSpan && (
                            <button
                              type="button"
                              className="mini-button secondary"
                              onClick={() =>
                                applyQuickFilter({
                                  appId: '',
                                  traceId: log.trace_id ?? '',
                                  spanId: log.span_id ?? '',
                                  parentSpanId: ''
                                })
                              }
                            >
                              Focus
                            </button>
                          )}
                          {log.trace_id && !hasTraceFilter && (
                            <button
                              type="button"
                              className="mini-button secondary"
                              onClick={() =>
                                applyQuickFilter({
                                  traceId: log.trace_id ?? '',
                                  spanId: '',
                                  parentSpanId: ''
                                })
                              }
                            >
                              Trace
                            </button>
                          )}
                          {log.parent_span_id && (
                            <button
                              type="button"
                              className="mini-button secondary"
                              onClick={() =>
                                applyQuickFilter({
                                  appId: '',
                                  traceId: log.trace_id ?? '',
                                  spanId: log.parent_span_id ?? '',
                                  parentSpanId: ''
                                })
                              }
                            >
                              Parent
                            </button>
                          )}
                          {log.span_id && (
                            <button
                              type="button"
                              className="mini-button secondary"
                              onClick={() =>
                                applyQuickFilter({
                                  appId: '',
                                  traceId: log.trace_id ?? '',
                                  parentSpanId: log.span_id ?? '',
                                  spanId: ''
                                })
                              }
                            >
                              Children
                            </button>
                          )}
                          <button
                            className="mini-button"
                            aria-expanded={Boolean(expanded[log.id])}
                            aria-controls={expanded[log.id] ? `log-viewer-detail-${log.id}` : undefined}
                            onClick={() => toggleExpanded(log.id)}
                          >
                            {expanded[log.id] ? 'Hide' : 'Details'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded[log.id] && (
                      <tr className="log-table__detail-row">
                        <td colSpan={8}>
                          <div className="log-detail" id={`log-viewer-detail-${log.id}`}>
                            {dockerRunDetails}
                            <div className="detail-grid">
                              <div>
                                <strong>Trace</strong>
                                <div className="mono">
                                  {log.trace_id ? (
                                    <button
                                      type="button"
                                      className="mini-button"
                                      onClick={() =>
                                        applyQuickFilter({
                                          appId: '',
                                          traceId: log.trace_id ?? '',
                                          spanId: '',
                                          parentSpanId: ''
                                        })
                                      }
                                    >
                                      {log.trace_id}
                                    </button>
                                  ) : (
                                    '-'
                                  )}
                                </div>
                              </div>
                              <div>
                                <strong>Span</strong>
                                <div className="mono">
                                  {log.span_id ? (
                                    <div className="stack">
                                      <button
                                        type="button"
                                        className="mini-button"
                                        onClick={() =>
                                          applyQuickFilter({
                                            appId: '',
                                            spanId: log.span_id ?? ''
                                          })
                                        }
                                      >
                                        {log.span_id}
                                      </button>
                                    </div>
                                  ) : (
                                    '-'
                                  )}
                                </div>
                              </div>
                              <div>
                                <strong>Request</strong>
                                <div className="mono">{log.request_id ?? '-'}</div>
                              </div>
                            </div>
                            <div>
                              <strong>Fields</strong>
                              {renderFields(log.fields ?? undefined, excludeKeys)}
                            </div>
                            {log.raw && (
                              <div>
                                <strong>Raw</strong>
                                <pre>{JSON.stringify(log.raw, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <FixBugDialog
        open={fixBugOpen}
        onClose={() => setFixBugOpen(false)}
        apiClient={apiClient}
        models={models}
        apps={apps}
        defaultAppId={apps.find((app) => app.is_self)?.id}
        defaultTitle="Fix Bug"
        defaultPrompt={`Please investigate and fix the issue shown in the logs.

Current query filters:
${[
  appId && `App: ${apps.find(a => a.id === appId)?.name || appId}`,
  sourceFilter && `Source: ${sourceFilter === 'true' ? 'from app' : 'control plane / OTLP'}`,
  traceId && `Trace ID: ${traceId}`,
  requestId && `Request ID: ${requestId}`,
  spanId && `Span ID: ${spanId}`,
  parentSpanId && `Parent Span ID: ${parentSpanId}`,
  kind && `Kind: ${kind}`,
  level && `Level: ${level}`,
  levelMin && `Min Level: ${levelMin}`,
  search && `Search: ${search}`
].filter(Boolean).join('\n') || 'No specific filters'}

You can use the MCP tools to query logs:
- Use get_logs with the filters above to investigate
- Use get_log_detail to get full details of specific log entries

Focus on understanding what went wrong from the error messages, relevant traces and spans, and the root cause.

${FIX_BUG_WORKFLOW_INSTRUCTIONS}`}
        onCreated={(created: Conversation) => navigate(`/conversations/${created.id}`)}
        setError={setError}
      />
    </section>
  );
}
